import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

const QUEST_LINE_TYPES = ['story', 'paradise_pass', 'daily', 'weekly', 'event', 'side'];

export default function QuestLineEditDialog({ 
  open, 
  onClose, 
  questLine, 
  allQuestLines = [],
  onSave 
}) {
  const [formData, setFormData] = useState({
    displayName: '',
    description: '',
    questLineType: 'story',
    requirements: [],
    isActive: 1,
    sortOrder: 0,
    targetPassTier: '',
    targetDay: '',
    conversationIds: {
      offerConversationId: null,
      startConversationId: null,
      endConversationId: null
    }
  });

  useEffect(() => {
    if (questLine) {
      setFormData({
        displayName: questLine.displayName || '',
        description: questLine.description || '',
        questLineType: questLine.questLineType || 'story',
        requirements: parseJSON(questLine.requirements),
        isActive: questLine.isActive ?? 1,
        sortOrder: questLine.sortOrder || 0,
        targetPassTier: questLine.targetPassTier || '',
        targetDay: questLine.targetDay || '',
        conversationIds: questLine.conversationIds || {
          offerConversationId: null,
          startConversationId: null,
          endConversationId: null
        }
      });
    } else {
      // Reset for new quest line
      setFormData({
        displayName: '',
        description: '',
        questLineType: 'story',
        requirements: [],
        isActive: 1,
        sortOrder: 0,
        targetPassTier: '',
        targetDay: '',
        conversationIds: {
          offerConversationId: null,
          startConversationId: null,
          endConversationId: null
        }
      });
    }
  }, [questLine, open]);

  const parseJSON = (field) => {
    try {
      if (typeof field === 'string') return JSON.parse(field);
      return field || [];
    } catch {
      return [];
    }
  };

  const handleAddRequirement = () => {
    setFormData(prev => ({
      ...prev,
      requirements: [...prev.requirements, { dataPoint: 'QuestLineComplete', amount: 1, targetId: '' }]
    }));
  };

  const handleRemoveRequirement = (index) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index)
    }));
  };

  const handleRequirementChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.map((req, i) => 
        i === index ? { ...req, [field]: value } : req
      )
    }));
  };

  const handleSave = () => {
    onSave(formData);
  };

  // Filter out current quest line from requirements dropdown
  const availableQuestLines = allQuestLines.filter(ql => ql.id !== questLine?.id);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{questLine ? 'Edit Quest Line' : 'Create New Quest Line'}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label="Display Name"
            fullWidth
            value={formData.displayName}
            onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
          />

          <FormControl fullWidth>
            <InputLabel>Quest Line Type</InputLabel>
            <Select
              value={formData.questLineType}
              label="Quest Line Type"
              onChange={(e) => setFormData(prev => ({ ...prev, questLineType: e.target.value }))}
            >
              {QUEST_LINE_TYPES.map(type => (
                <MenuItem key={type} value={type}>
                  {type.replace('_', ' ').toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Sort Order"
              type="number"
              fullWidth
              value={formData.sortOrder}
              onChange={(e) => setFormData(prev => ({ ...prev, sortOrder: parseInt(e.target.value) || 0 }))}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive === 1 || formData.isActive === true}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked ? 1 : 0 }))}
                />
              }
              label="Active"
            />
          </Stack>

          {/* Paradise Pass Fields */}
          {(formData.questLineType === 'paradise_pass' || formData.questLineType === 'daily') && (
            <Stack direction="row" spacing={2}>
              <TextField
                label="Target Pass Tier"
                fullWidth
                value={formData.targetPassTier}
                onChange={(e) => setFormData(prev => ({ ...prev, targetPassTier: e.target.value }))}
                placeholder="e.g., Free, Premium"
              />
              <TextField
                label="Target Day"
                type="date"
                fullWidth
                value={formData.targetDay}
                onChange={(e) => setFormData(prev => ({ ...prev, targetDay: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          )}

          {/* Requirements */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2">Requirements (Quest Line must be completed first)</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={handleAddRequirement}>
                Add Requirement
              </Button>
            </Stack>
            <Stack spacing={1}>
              {formData.requirements.map((req, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Data Point</InputLabel>
                    <Select
                      value={req.dataPoint || 'QuestLineComplete'}
                      label="Data Point"
                      onChange={(e) => handleRequirementChange(index, 'dataPoint', e.target.value)}
                    >
                      <MenuItem value="QuestLineComplete">Quest Line Complete</MenuItem>
                      <MenuItem value="TycoonBuilderLevelReached">Tycoon Level</MenuItem>
                    </Select>
                  </FormControl>

                  {req.dataPoint === 'QuestLineComplete' ? (
                    <FormControl size="small" sx={{ flexGrow: 1 }}>
                      <InputLabel>Required Quest Line</InputLabel>
                      <Select
                        value={req.targetId || ''}
                        label="Required Quest Line"
                        onChange={(e) => handleRequirementChange(index, 'targetId', e.target.value)}
                      >
                        {availableQuestLines.map(ql => (
                          <MenuItem key={ql.id} value={ql.id}>
                            {ql.displayName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      label="Target ID"
                      size="small"
                      value={req.targetId || ''}
                      onChange={(e) => handleRequirementChange(index, 'targetId', e.target.value)}
                      sx={{ flexGrow: 1 }}
                    />
                  )}

                  <TextField
                    label="Amount"
                    type="number"
                    size="small"
                    value={req.amount || 1}
                    onChange={(e) => handleRequirementChange(index, 'amount', parseInt(e.target.value) || 1)}
                    sx={{ width: '100px' }}
                  />

                  <IconButton size="small" color="error" onClick={() => handleRemoveRequirement(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              ))}
              {formData.requirements.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No requirements - this quest line will be available immediately
                </Typography>
              )}
            </Stack>
          </Box>

          {/* Conversation IDs */}
          <Box>
            <Typography variant="subtitle2" mb={1}>Conversation IDs</Typography>
            <Stack spacing={2}>
              <TextField
                label="Offer Conversation ID"
                fullWidth
                value={formData.conversationIds?.offerConversationId || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  conversationIds: {
                    ...prev.conversationIds,
                    offerConversationId: e.target.value || null
                  }
                }))}
                placeholder="GUID for conversation shown when quest line is offered"
              />
              <TextField
                label="Start Conversation ID"
                fullWidth
                value={formData.conversationIds?.startConversationId || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  conversationIds: {
                    ...prev.conversationIds,
                    startConversationId: e.target.value || null
                  }
                }))}
                placeholder="GUID for conversation shown when quest line starts"
              />
              <TextField
                label="End Conversation ID"
                fullWidth
                value={formData.conversationIds?.endConversationId || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  conversationIds: {
                    ...prev.conversationIds,
                    endConversationId: e.target.value || null
                  }
                }))}
                placeholder="GUID for conversation shown when quest line ends"
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
}

